import React, { useState, useEffect, useRef } from 'react';
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import GlobalTable from "../utils/GlobalTable";
import getParamsEnv from "../../utils/getParamsEnv";
import axios from 'axios';
import { FaUserPlus, FaEdit, FaTrash, FaCamera, FaIdCard, FaBuilding } from 'react-icons/fa';

const FrmTrabajador = () => {
    const { tokenSeguridad } = useSesion();
    const { handleOpenAlert, handleOpenLoader, handleCloseLoader } = useLayout();
    const { API_URL_BASE } = getParamsEnv();

    // States
    const [trabajadores, setTrabajadores] = useState([]);
    const [puestos, setPuestos] = useState([]);
    const [regimenes, setRegimenes] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [tabActive, setTabActive] = useState('personal'); // 'personal' | 'laboral'
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'form'

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    // Filter
    const [filterText, setFilterText] = useState("");

    // Default Form State
    const initialForm = {
        PrimerNombre: '', SegundoNombre: '', ApellidoPaterno: '', ApellidoMaterno: '',
        TipoDocumento: 'DNI', NumeroDocumento: '', FechaNacimiento: '', Genero: 'M',
        Direccion: '', Telefono: '', EmailPersonal: '',
        CodigoEmpleado: '', FechaContratacion: '', FechaBaja: '',
        IdPuesto: '', IdRegimen: '', SalarioBaseMensual: '', EstadoLaboral: 'Activo'
    };
    const [formData, setFormData] = useState(initialForm);
    const [fotoPreview, setFotoPreview] = useState(null); // URL for preview
    const [fotoFile, setFotoFile] = useState(null); // File object or Base64 string

    // Refs
    const fileInputRef = useRef(null);

    // Initial Load
    useEffect(() => {
        if (tokenSeguridad) {
            fetchTrabajadores();
            fetchCatalogos();
        }
    }, [tokenSeguridad]);

    const fetchTrabajadores = async () => {
        try {
            handleOpenLoader("Cargando trabajadores...");
            const res = await axios.get(`${API_URL_BASE}trabajador`, {
                headers: { Authorization: `Bearer ${tokenSeguridad}` }
            });
            // Format for Table
            if (!Array.isArray(res.data)) {
                throw new Error(res.data?.message || "La respuesta del servidor no es una lista válida.");
            }
            const formatted = res.data.map(t => ({
                id: t.IdTrabajador,
                NombreCompleto: t.Persona ? `${t.Persona.PrimerNombre} ${t.Persona.ApellidoPaterno}` : 'Desconocido',
                DNI: t.Persona ? t.Persona.NumeroDocumento : '-',
                Puesto: t.Puesto?.NombrePuesto || '-',
                Regimen: t.Regimen?.NombreRegimen || '-',
                Estado: t.EstadoLaboral,
                Foto: t.Foto
            }));
            setTrabajadores(formatted);
        } catch (error) {
            if (error.response?.status !== 401) {
                handleOpenAlert("Error al cargar trabajadores: " + error.message, "error");
            }
        } finally {
            handleCloseLoader();
        }
    };

    const fetchCatalogos = async () => {
        try {
            const [resPuestos, resRegimenes] = await Promise.all([
                axios.get(`${API_URL_BASE}puesto`, { headers: { Authorization: `Bearer ${tokenSeguridad}` } }),
                axios.get(`${API_URL_BASE}regimen`, { headers: { Authorization: `Bearer ${tokenSeguridad}` } })
            ]);
            const puestosData = Array.isArray(resPuestos.data) ? resPuestos.data : [];
            const regimenesData = Array.isArray(resRegimenes.data) ? resRegimenes.data : [];

            if (!Array.isArray(resPuestos.data)) console.warn("Puestos no es un array:", resPuestos.data);
            if (!Array.isArray(resRegimenes.data)) console.warn("Regimenes no es un array:", resRegimenes.data);

            setPuestos(puestosData);
            setRegimenes(regimenesData);
        } catch (error) {
            console.error("Error loading catalogs", error);
            if (error.response?.status !== 401) {
                handleOpenAlert("Error cargando catálogos: " + error.message, "error");
            }
        }
    };

    // Handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFotoFile(reader.result); // Base64 string
                setFotoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSave = async () => {
        // Validate required fields
        if (!formData.PrimerNombre || !formData.ApellidoPaterno || !formData.NumeroDocumento || !formData.IdPuesto || !formData.IdRegimen) {
            handleOpenAlert("Complete los campos obligatorios (*)", "warning");
            return;
        }

        try {
            handleOpenLoader("Guardando...");
            const payload = { ...formData, FotoBuffer: fotoFile };

            if (isEditing) {
                await axios.put(`${API_URL_BASE}trabajador/${currentId}`, payload, {
                    headers: { Authorization: `Bearer ${tokenSeguridad}` }
                });
                handleOpenAlert("Trabajador actualizado correctamente", "success");
            } else {
                await axios.post(`${API_URL_BASE}trabajador`, payload, {
                    headers: { Authorization: `Bearer ${tokenSeguridad}` }
                });
                handleOpenAlert("Trabajador registrado correctamente", "success");
            }
            handleReset();
            fetchTrabajadores();
        } catch (error) {
            handleOpenAlert("Error al guardar: " + (error.response?.data?.message || error.message), "error");
        } finally {
            handleCloseLoader();
        }
    };

    const handleEdit = async (row) => {
        try {
            handleOpenLoader("Cargando detalles...");
            const res = await axios.get(`${API_URL_BASE}trabajador/${row.id}`, {
                headers: { Authorization: `Bearer ${tokenSeguridad}` }
            });
            const t = res.data;

            setFormData({
                PrimerNombre: t.Persona.PrimerNombre,
                SegundoNombre: t.Persona.SegundoNombre || '',
                ApellidoPaterno: t.Persona.ApellidoPaterno,
                ApellidoMaterno: t.Persona.ApellidoMaterno || '',
                TipoDocumento: t.Persona.TipoDocumento,
                NumeroDocumento: t.Persona.NumeroDocumento,
                FechaNacimiento: t.Persona.FechaNacimiento || '',
                Genero: t.Persona.Genero || 'M',
                Direccion: t.Persona.Direccion || '',
                Telefono: t.Persona.Telefono || '',
                EmailPersonal: t.Persona.EmailPersonal || '',
                CodigoEmpleado: t.CodigoEmpleado || '',
                FechaContratacion: t.FechaContratacion,
                FechaBaja: t.FechaBaja || '',
                IdPuesto: t.IdPuesto,
                IdRegimen: t.IdRegimen,
                SalarioBaseMensual: t.SalarioBaseMensual || '',
                EstadoLaboral: t.EstadoLaboral
            });

            // Convert Buffer to Base64 for Preview if existing
            if (t.Foto) {
                // If it comes as { type: 'Buffer', data: [...] } from Sequelize JSON
                if (t.Foto.type === 'Buffer') {
                    // Fix: Handle large buffers by processing in chunks
                    const bytes = new Uint8Array(t.Foto.data);
                    let binary = '';
                    const len = bytes.byteLength;
                    for (let i = 0; i < len; i++) {
                        binary += String.fromCharCode(bytes[i]);
                    }
                    const base64String = btoa(binary);
                    setFotoPreview(`data:image/png;base64,${base64String}`);
                } else {
                    // It might be already string if sent that way
                    setFotoPreview(t.Foto);
                }
            } else {
                setFotoPreview(null);
            }
            setFotoFile(undefined); // undefined means no change unless user picks new

            setIsEditing(true);
            setCurrentId(row.id);
            setViewMode('form');
        } catch (error) {
            console.error("Error al cargar detalles (Client):", error);
            handleOpenAlert("Error al cargar detalles: " + error.message, "error");
        } finally {
            handleCloseLoader();
        }
    };

    const handleDelete = (row) => {
        handleOpenAlert("Eliminación no implementada (Soft Delete recomendado)", "info");
    };

    const handleReset = () => {
        setFormData(initialForm);
        setFotoPreview(null);
        setFotoFile(null);
        setIsEditing(false);
        setCurrentId(null);
        setTabActive('personal');
    };

    // Columns for Table (GlobalTable format)
    const columns = [
        { header: 'Nombre Completo', accessor: 'NombreCompleto', render: (row) => <span className="font-medium text-gray-900">{row.NombreCompleto}</span> },
        { header: 'DNI', accessor: 'DNI', render: (row) => <span className="font-mono text-gray-600 group-hover:text-indigo-600 transition-colors">{row.DNI}</span> },
        {
            header: 'Puesto',
            accessor: 'Puesto',
            render: (row) => (
                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold border border-blue-100">
                    {row.Puesto}
                </span>
            )
        },
        { header: 'Régimen', accessor: 'Regimen', render: (row) => <span className="text-gray-500 text-sm">{row.Regimen}</span> },
        {
            header: 'Estado',
            accessor: 'Estado',
            render: (row) => (
                <span className={`px-2 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1
                    ${row.Estado === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                `}>
                    <span className={`w-1.5 h-1.5 rounded-full ${row.Estado === 'Activo' ? 'bg-green-600' : 'bg-red-600'}`}></span>
                    {row.Estado}
                </span>
            )
        },
    ];

    // Filter & Pagination Logic
    const filteredItems = trabajadores.filter(t =>
        t.NombreCompleto.toLowerCase().includes(filterText.toLowerCase()) ||
        t.DNI.includes(filterText)
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);


    return (
        <div className="h-full flex flex-col w-full">
            {viewMode === 'list' ? (
                <GlobalTable
                    storageKey="rrhh_trabajadores"
                    columns={columns}
                    data={trabajadores}
                    onCreate={() => { handleReset(); setViewMode('form'); }}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchPlaceholder="Buscar por Nombre, DNI, Puesto o Régimen..."
                />
            ) : (
                <div className="bg-white rounded-xl shadow-lg flex flex-col md:flex-row overflow-hidden flex-1 border border-gray-100 animate-fade-in-up">

                    {/* Left Panel: Photo & Summary */}
                    <div className="md:w-1/4 bg-gray-50/80 p-6 flex flex-col items-center border-r border-gray-100">
                        <div className="relative w-48 h-48 mb-6 group cursor-pointer">
                            <div className="w-full h-full rounded-full overflow-hidden border-4 border-white shadow-xl bg-gray-200 flex items-center justify-center transition-transform transform group-hover:scale-105">
                                {fotoPreview ? (
                                    <img src={fotoPreview} alt="Foto" className="w-full h-full object-cover" />
                                ) : (
                                    <FaIdCard className="text-6xl text-gray-300" />
                                )}
                            </div>
                            <div
                                className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-sm"
                                onClick={() => fileInputRef.current.click()}
                            >
                                <FaCamera className="text-white text-3xl drop-shadow-md" />
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>
                        <h3 className="text-xl font-bold text-center text-gray-800">
                            {formData.PrimerNombre || 'Nuevo'} {formData.ApellidoPaterno || 'Trabajador'}
                        </h3>
                        <p className="text-sm font-medium text-indigo-500 bg-indigo-50 px-3 py-1 rounded-full mt-2">
                            {formData.NumeroDocumento || 'DNI ...'}
                        </p>
                    </div>

                    {/* Right Panel: Tabs & Form */}
                    <div className="flex-1 flex flex-col">
                        {/* Tabs Header */}
                        <div className="flex border-b border-gray-100 bg-white">
                            <button
                                className={`px-8 py-4 font-medium text-sm transition-all relative
                                    ${tabActive === 'personal' ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
                                `}
                                onClick={() => setTabActive('personal')}
                            >
                                <FaIdCard className="inline mr-2 mb-0.5" /> Datos Personales
                                {tabActive === 'personal' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 rounded-t-full"></span>}
                            </button>
                            <button
                                className={`px-8 py-4 font-medium text-sm transition-all relative
                                    ${tabActive === 'laboral' ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}
                                `}
                                onClick={() => setTabActive('laboral')}
                            >
                                <FaBuilding className="inline mr-2 mb-0.5" /> Datos Laborales
                                {tabActive === 'laboral' && <span className="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 rounded-t-full"></span>}
                            </button>
                        </div>

                        {/* Form Content */}
                        <div className="p-8 flex-1 overflow-y-auto bg-white custom-scrollbar">
                            {tabActive === 'personal' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                                    {/* Personal Inputs */}
                                    <Input label="Primer Nombre *" name="PrimerNombre" value={formData.PrimerNombre} onChange={handleInputChange} />
                                    <Input label="Segundo Nombre" name="SegundoNombre" value={formData.SegundoNombre} onChange={handleInputChange} />
                                    <Input label="Apellido Paterno *" name="ApellidoPaterno" value={formData.ApellidoPaterno} onChange={handleInputChange} />
                                    <Input label="Apellido Materno" name="ApellidoMaterno" value={formData.ApellidoMaterno} onChange={handleInputChange} />

                                    <div className="flex flex-col">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Tipo Documento</label>
                                        <select name="TipoDocumento" value={formData.TipoDocumento} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all">
                                            <option value="DNI">DNI</option>
                                            <option value="CE">CE</option>
                                            <option value="PASAPORTE">Pasaporte</option>
                                        </select>
                                    </div>
                                    <Input label="Número Documento *" name="NumeroDocumento" value={formData.NumeroDocumento} onChange={handleInputChange} />

                                    <Input label="Fecha Nacimiento" type="date" name="FechaNacimiento" value={formData.FechaNacimiento} onChange={handleInputChange} />

                                    <div className="flex flex-col">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Género</label>
                                        <select name="Genero" value={formData.Genero} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all">
                                            <option value="M">Masculino</option>
                                            <option value="F">Femenino</option>
                                        </select>
                                    </div>

                                    <div className="md:col-span-2">
                                        <Input label="Dirección" name="Direccion" value={formData.Direccion} onChange={handleInputChange} />
                                    </div>
                                    <Input label="Teléfono" name="Telefono" value={formData.Telefono} onChange={handleInputChange} />
                                    <Input label="Email Personal" type="email" name="EmailPersonal" value={formData.EmailPersonal} onChange={handleInputChange} />
                                </div>
                            )}

                            {tabActive === 'laboral' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                                    <Input label="Código Empleado" name="CodigoEmpleado" value={formData.CodigoEmpleado} onChange={handleInputChange} />
                                    <Input label="Fecha Contratación *" type="date" name="FechaContratacion" value={formData.FechaContratacion} onChange={handleInputChange} />

                                    <div className="flex flex-col">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Puesto *</label>
                                        <select name="IdPuesto" value={formData.IdPuesto} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all">
                                            <option value="">-- Seleccione --</option>
                                            {puestos.map(p => (
                                                <option key={p.IdPuesto} value={p.IdPuesto}>{p.NombrePuesto}</option>
                                            ))}
                                        </select>
                                        {puestos.length === 0 && <span className="text-xs text-red-500 mt-1">No hay puestos registrados.</span>}
                                    </div>

                                    <div className="flex flex-col">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Régimen Laboral *</label>
                                        <select name="IdRegimen" value={formData.IdRegimen} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all">
                                            <option value="">-- Seleccione --</option>
                                            {regimenes.map(r => (
                                                <option key={r.IdRegimen} value={r.IdRegimen}>{r.NombreRegimen}</option>
                                            ))}
                                        </select>
                                        {regimenes.length === 0 && <span className="text-xs text-red-500 mt-1">No hay regímenes registrados.</span>}
                                    </div>

                                    <Input label="Salario Base" type="number" name="SalarioBaseMensual" value={formData.SalarioBaseMensual} onChange={handleInputChange} />

                                    <div className="flex flex-col">
                                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">Estado</label>
                                        <select name="EstadoLaboral" value={formData.EstadoLaboral} onChange={handleInputChange} className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all">
                                            <option value="Activo">Activo</option>
                                            <option value="Inactivo">Inactivo</option>
                                            <option value="Licencia">Licencia</option>
                                        </select>
                                    </div>

                                    <Input label="Fecha Baja" type="date" name="FechaBaja" value={formData.FechaBaja} onChange={handleInputChange} />
                                </div>
                            )}
                        </div>

                        {/* Footer Buttons */}
                        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                            <button
                                onClick={() => { handleReset(); setViewMode('list'); }}
                                className="px-6 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-white border border-transparent hover:border-gray-200 rounded-lg transition-all font-medium"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSave}
                                className="px-8 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-md hover:shadow-lg transition-all flex items-center gap-2 font-medium"
                            >
                                <FaUserPlus /> Guardar Trabajador
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Helper Input Component with improved styling
const Input = ({ label, type = "text", ...props }) => (
    <div className="flex flex-col">
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 ml-1">{label}</label>
        <input
            type={type}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
            {...props}
        />
    </div>
);

export default FrmTrabajador;
